<div class="projectable-form-wrapper ipsec-tunneler-form" id="IpsecTunnelerPage">

  <lib-form-header
    [data]="model"
    [title]="'Create IPsec Tunneler'"
    [actionButtonText]="processing ? 'Saving...' : 'Create'"
    [saveDisabled]="processing || !hasMinimumProposals"
    [showHeaderToggle]="false"
    [showHeaderButton]="!isComplete || !pskRevealed"
    [moreActions]="[]"
    (actionRequested)="headerActionRequested($event)"
  ></lib-form-header>

  <div class="projectable-form-container ipsec-form-container">

    <app-loading-indicator [isLoading]="isLoading" class="load-indicator"></app-loading-indicator>

    @if (!isLoading) {
      <div class="ipsec-main-column">

        <div class="form-subtitle">
          Deploys a bridge between an IPsec tunnel and this OpenZiti network.
        </div>

        @if (!isComplete || !pskRevealed) {

          <lib-form-field-container [title]="'Tunneler Name'" [label]="'REQUIRED'">
            <input
              type="text"
              class="form-field-input"
              [(ngModel)]="model.name"
              (ngModelChange)="onNameChange()"
              id="IpsecTunnelerName"
              placeholder="e.g. customer-tunnel-1"
            />
            @if (errors['name']) {
              <span class="form-field-error">{{ errors['name'] }}</span>
            }
          </lib-form-field-container>

          <!-- AWS region and VM size are intentionally NOT rendered on the create form.
               Region is locked to us-east-1 until additional IPsec VPCs are provisioned,
               and size is locked to 'medium' for the POC. Both values are still set on
               the model in initModel() so the create request carries them. Restore the
               form fields here once multi-region / multi-size selection is supported. -->


          <lib-form-field-container [title]="'Customer Public IP (IPv4)'" [label]="'REQUIRED'">
            <input
              type="text"
              class="form-field-input"
              [(ngModel)]="model.customerPublicIp"
              id="IpsecTunnelerCustomerPublicIp"
              placeholder="e.g. 203.0.113.42"
            />
            @if (errors['customerPublicIp']) {
              <span class="form-field-error">{{ errors['customerPublicIp'] }}</span>
            }

            <div class="form-field-label-container">
              <div class="form-field-header">
                <div class="form-field-title-container">
                  <span class="form-field-title">Customer Peer CIDRs</span>
                </div>
                <span class="form-field-label">REQUIRED</span>
              </div>
              <div class="cidr-chips-container" id="IpsecTunnelerPeerCidrs">
                @for (cidr of model.customerPeerCidrs; track cidr) {
                  <span class="cidr-chip" [id]="'IpsecTunnelerPeerCidrChip_' + cidr">
                    {{ cidr }}
                    <span
                      class="chip-remove"
                      (click)="removeCidr(cidr)"
                      [attr.aria-label]="'Remove ' + cidr"
                    >&times;</span>
                  </span>
                }
                <input
                  type="text"
                  class="form-field-input cidr-input"
                  [(ngModel)]="customerPeerCidrInput"
                  (keydown.enter)="$event.preventDefault(); addCidr(customerPeerCidrInput)"
                  placeholder="10.0.0.0/24"
                  id="IpsecTunnelerPeerCidrInput"
                />
                <button
                  type="button"
                  class="add-button"
                  (click)="addCidr(customerPeerCidrInput)"
                  id="IpsecTunnelerPeerCidrAdd"
                >+</button>
              </div>
              <div class="form-field-hint">
                Each entry must be a canonical IPv4 CIDR (host bits zero,
                e.g. <code>10.20.0.0/24</code> or <code>10.19.140.76/32</code>).
              </div>
              @if (errors['customerPeerCidrs']) {
                <span class="form-field-error">{{ errors['customerPeerCidrs'] }}</span>
              }
            </div>
          </lib-form-field-container>

          <!-- Show advanced toggle — uses ziti-console-lib's standard form-field-toggle
               (the same one identity-form uses for SHOW MORE OPTIONS). orientation='row'
               renders the label inline with the toggle pill on the right. -->
          <lib-form-field-toggle
            id="IpsecTunnelerShowMoreToggle"
            [label]="'SHOW MORE OPTIONS'"
            [orientation]="'row'"
            [labelOn]="'ON'"
            [labelOff]="'OFF'"
            [toggleOn]="showMore"
            (toggleOnChange)="showMore = $event"
          ></lib-form-field-toggle>

          @if (showMore) {
            <h3 class="section-heading">Encryption Proposals</h3>

            <div class="form-field-hint encryption-intro">
              Select which IKE and ESP cipher proposals this tunneler may negotiate.
              At least one entry from each group is required.
            </div>

            @if (proposalsLoading) {
              <div class="form-field-hint">Loading proposal catalog…</div>
            }

            @if (proposalsLoadFailed) {
              <div class="form-field-error proposal-load-error">
                Proposal catalog is unavailable — cannot create or update the tunneler.
                Verify the core-management service is reachable and that the catalog
                endpoint <code>GET /v3/ipsec-proposals</code> returns the seeded
                entries, then reopen this form.
              </div>
            }

            <lib-form-field-container [title]="'IKE Proposals (Phase 1)'" [label]="'REQUIRED'" id="IpsecTunnelerIkeProposals">
              <div class="proposal-list">
                @for (p of ikeProposalCatalog; track p.value) {
                  <mat-checkbox
                    class="round-checkbox proposal-row"
                    [checked]="ikeProposalSelected[p.value]"
                    (change)="onToggleIkeProposal(p.value)"
                    [id]="'IpsecTunnelerIke_' + p.value"
                  >
                    <span class="proposal-label">{{ p.label }}</span>
                    <span class="proposal-value" [matTooltip]="p.value">{{ p.value }}</span>
                  </mat-checkbox>
                }
              </div>
              @if (errors['ikeProposals']) {
                <span class="form-field-error">{{ errors['ikeProposals'] }}</span>
              } @else if (!proposalsLoading && ikeProposalCatalog.length > 0 && ikeSelectionCount < 1) {
                <span class="form-field-error">At least one IKE proposal must be selected</span>
              }
            </lib-form-field-container>

            <lib-form-field-container [title]="'ESP Proposals'" [label]="'REQUIRED'" id="IpsecTunnelerEspProposals">
              <div class="proposal-list">
                @for (p of espProposalCatalog; track p.value) {
                  <mat-checkbox
                    class="round-checkbox proposal-row"
                    [checked]="espProposalSelected[p.value]"
                    (change)="onToggleEspProposal(p.value)"
                    [id]="'IpsecTunnelerEsp_' + p.value"
                  >
                    <span class="proposal-label">{{ p.label }}</span>
                    <span class="proposal-value" [matTooltip]="p.value">{{ p.value }}</span>
                  </mat-checkbox>
                }
              </div>
              @if (errors['espProposals']) {
                <span class="form-field-error">{{ errors['espProposals'] }}</span>
              } @else if (!proposalsLoading && espProposalCatalog.length > 0 && espSelectionCount < 1) {
                <span class="form-field-error">At least one ESP proposal must be selected</span>
              }
            </lib-form-field-container>
          }

        }

      </div>
    }
  </div>
</div>
